import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { translations, languages } from '../mock/translations';
import { reloadIubendaWithLang } from '../lib/iubenda';

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const urlLang = new URLSearchParams(location.search).get('lang');
  // ?lang=xx (hreflang URLs, shared links) wins over the saved preference.
  const [lang, setLang] = useState(() => {
    if (urlLang && translations[urlLang]) return urlLang;
    const saved = localStorage.getItem('lg_lang');
    return saved && translations[saved] ? saved : 'it';
  });

  useEffect(() => {
    if (urlLang && translations[urlLang] && urlLang !== lang) setLang(urlLang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlLang]);

  // Keep an existing ?lang in the address bar in sync when the visitor switches language.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const current = params.get('lang');
    if (!current || (current === lang && lang !== 'it')) return;
    if (lang === 'it') params.delete('lang'); else params.set('lang', lang);
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => {
    localStorage.setItem('lg_lang', lang);
    document.documentElement.lang = lang;
    // Keep the Iubenda cookie banner in sync with the app language. This
    // tears down the widget and re-injects the script if the language
    // actually changed (no-op otherwise).
    reloadIubendaWithLang(lang);
  }, [lang]);

  const t = translations[lang] || translations.it;

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, languages }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
};
